import clsx from 'clsx';
import {useRef} from 'react';
import {motion} from 'framer-motion';
import {useState} from 'react';
import {Scrollama, Step} from 'react-scrollama';

import type {SanityModuleImageBlock} from '../../types';

import ModuleWrapper from './ModuleWrapper.client';
import PortableText from '../portableText/PortableText.client';
import {Image, Link} from '@shopify/hydrogen';

import {pathVars} from '../ui/animationVars';

export default function ImageBlockModule({
  module,
}: {
  module: SanityModuleImageBlock;
}) {
  const {config, image, layout, animated, text, link, label, mini}: any =
    module;

  //animation setup
  //Scroll trigger
  const [progress, setProgress] = useState<any>(null);

  // This callback fires when a Step hits the offset threshold. It receives the
  // data prop of the step, which in this demo stores the index of the step.

  const onStepProgress = ({progress: onProgess}: any) => {
    setProgress(Math.round((onProgess * 1000) / 10));
  };

  const [isActive, setIsActive] = useState<boolean>(false);

  //click link ref
  const linkRef = useRef<any>(null);

  const url = image?.asset?.url || image?.url;

  return (
    // animated image block
    animated ? (
      <>
        {/* Desktop */}
        <ModuleWrapper {...config} className="hidden h-[200vh] !p-0 lg:block">
          {/* Scroll tracking */}
          <Scrollama
            offset={0.5}
            debug={false}
            progress
            onStepProgress={onStepProgress}
          >
            <Step data={1} key={1}>
              <div className="absolute top-[50vh] z-30 col-start-1 col-end-2 row-start-1 flex h-screen flex-col items-center justify-center text-orange opacity-0">
                <div>Progress: {progress}%</div>
              </div>
            </Step>
          </Scrollama>
          <motion.div
            style={{padding: `${100 - progress}px`}}
            className="sticky top-0 h-screen"
          >
            <motion.div
              style={{borderRadius: 15 - progress / 8}}
              className="relative flex h-full w-full items-center justify-center overflow-hidden"
            >
              <Image
                width="inherit"
                height="inherit"
                alt="Nature"
                onClick={() => linkRef?.current && linkRef.current.click()}
                onMouseEnter={() => setIsActive(true)}
                onMouseLeave={() => setIsActive(false)}
                data={{url}}
                loading="lazy"
                className={`${
                  linkRef?.current && 'cursor-pointer'
                } absolute top-0 left-0 h-full w-full object-cover`}
              />
              {(text || link) && (
                <motion.div
                  style={{opacity: progress / 100}}
                  className="pointer-events-none absolute top-0 left-0 h-full w-full bg-black bg-opacity-50 object-cover"
                />
              )}
              <motion.div
                style={{
                  opacity: progress / 100,
                  scale: 0.8 + (progress / 1000) * 2,
                }}
                className="pointer-events-none absolute w-full max-w-[850px] px-gutter text-center !text-white md:px-0"
              >
                <PortableText blocks={text} />
                <div>
                  {link && (
                    <Link
                      ref={linkRef}
                      to={link?.slug}
                      className="blockH8 mx-auto mt-6 flex items-center justify-center gap-x-3 font-bold"
                    >
                      <svg
                        width="32"
                        height="29"
                        viewBox="0 0 32 29"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15.7539 8.87207C15.7539 12.9064 16.2366 16.8979 16.2366 20.94"
                          stroke="#F27021"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M11.8945 15.1476C14.8677 14.2666 17.9885 14.1821 21.0662 14.1821"
                          stroke="#F27021"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <motion.path
                          variants={pathVars}
                          initial="initial"
                          animate={isActive ? 'animate' : 'initial'}
                          d="M20.5829 2.11412C15.2459 1.02247 10.5927 0.987209 5.75275 4.09862C-1.58016 8.81264 1.68348 19.1623 7.17408 23.9973C15.2855 31.1402 26.2679 25.5956 29.4595 16.2202C32.5936 7.01379 23.1674 4.22717 16.2384 2.59684"
                          stroke="#F27021"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      <span>{link?.title}</span>
                    </Link>
                  )}
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </ModuleWrapper>

        {/* Mobile */}
        <ModuleWrapper {...config} className="px-gutter pb-space-m lg:hidden">
          <Image
            width="inherit"
            height="inherit"
            alt="Nature"
            data={{url}}
            loading="lazy"
            className="w-full rounded-md"
          />
          <PortableText blocks={text} className="mt-10 text-center" />
          {link && (
            <Link
              to={link?.slug}
              className="blockH8 mx-auto mt-7 flex items-center justify-center gap-x-3 font-bold"
            >
              <svg
                width="32"
                height="29"
                viewBox="0 0 32 29"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.7539 8.87207C15.7539 12.9064 16.2366 16.8979 16.2366 20.94"
                  stroke="#F27021"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M11.8945 15.1476C14.8677 14.2666 17.9885 14.1821 21.0662 14.1821"
                  stroke="#F27021"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M20.5829 2.11412C15.2459 1.02247 10.5927 0.987209 5.75275 4.09862C-1.58016 8.81264 1.68348 19.1623 7.17408 23.9973C15.2855 31.1402 26.2679 25.5956 29.4595 16.2202C32.5936 7.01379 23.1674 4.22717 16.2384 2.59684"
                  stroke="#F27021"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span>{link?.title}</span>
            </Link>
          )}
        </ModuleWrapper>
      </>
    ) : (
      // Regular image block
      <ModuleWrapper {...config} className="grid grid-cols-14">
        {label && (
          <div
            className={clsx([
              'mb-6 grid lg:mb-12 lg:grid-cols-2',
              layout === 'center' || !layout
                ? 'col-start-2 col-end-14 md:col-start-3 md:col-end-13'
                : 'col-start-1 col-end-15',
            ])}
          >
            <div className="col-start-2 flex w-full gap-x-6">
              <svg
                className="h-full w-16"
                width="38"
                height="50"
                viewBox="0 0 38 50"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.9562 34.3155C16.9562 34.3155 13.868 40.8499 13.5135 41.8664C13.0124 43.3035 11.2056 48.2145 10.976 47.2165C10.3812 44.6315 8.21293 42.5552 6.2176 40.9411C5.18631 40.1069 2.84931 37.9328 2.84931 37.9328"
                  stroke="#F27021"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
                <path
                  d="M32.7927 3.82167C22.9561 8.98269 15.8415 16.0387 12.3067 26.9759C11.788 28.581 9.07223 43.9544 11.3185 43.4876"
                  stroke="#F27021"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
              <h6 className="richH6 font-bold">{label}</h6>
            </div>
          </div>
        )}
        <Image
          width="inherit"
          height="inherit"
          alt="goodnature trap"
          data={{url: url}}
          loading="lazy"
          className={clsx([
            mini ? 'mx-auto my-10 max-h-[120px] w-auto' : 'w-full ',
            layout === 'center' || !layout
              ? 'col-start-2 col-end-14 rounded-lg md:col-start-3 md:col-end-13'
              : 'col-start-1 col-end-15',
          ])}
        />
      </ModuleWrapper>
    )
  );
}
